import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useCallback, useContext } from 'react';
import { AlertContext } from '../contexts/alert-context';
import type { CreateOrder, Order } from '../data/order';
import { OrderStatus } from '../data/order';
import type { OrderItem } from '../data/order-item';
import type { OrderOptions } from '../data/order-options';
import type { Store } from '../data/store';
import type { Vendor } from '../data/vendor';
import { compare } from '../helpers/column-sort';
import { ordersQueryOptions, writeOrderCache } from '../query/query-options';
import { storeLayout } from '../router';
import { classifyOrderConflict, type ItemUpdate } from '../services/stores';

export function useOrders() {
  const { addErrorAlert } = useContext(AlertContext);
  const { store, storeService } = storeLayout.useRouteContext();

  const { data: orders = [], isLoading } = useQuery({
    ...ordersQueryOptions(store.id),
    queryFn: () => {
      return storeService.getOrdersByStore(store.id).catch((error: Error) => {
        addErrorAlert(`Invalid orders: ${error.message}`, { cause: error });

        throw error;
      });
    },
  });

  return {
    orders,
    // Only show loading if we don't have data AND we're actually loading (not just fetching)
    loading: orders.length === 0 && isLoading,
  };
}

export function useDraftOrders() {
  const { orders, loading } = useOrders();

  const draftOrdersByVendor = useCallback(
    (vendorId: string) => {
      return orders
        .filter((order) => order.vendor_id === vendorId && order.status === OrderStatus.Draft)
        .sort((a, b) => compare(new Date(b.updated_at), new Date(a.updated_at)));
    },
    [orders]
  );

  return { draftOrdersByVendor, loading };
}

export function useGetOrder() {
  const { addErrorAlert } = useContext(AlertContext);
  const { storeService } = storeLayout.useRouteContext();
  const queryClient = useQueryClient();

  return useCallback(
    (storeId: string, orderId: string) => {
      return storeService
        .getOrderByStore(storeId, orderId)
        .then((order) => {
          queryClient.setQueryData(['stores', storeId, 'orders'], (oldOrders: Order[] = []) => {
            return oldOrders.map((oldOrder) => {
              if (oldOrder.id === order.id) {
                return order;
              }

              return oldOrder;
            });
          });

          return order;
        })
        .catch((error: Error) => {
          addErrorAlert(error.message, { cause: error });

          throw error;
        });
    },
    [storeService, addErrorAlert, queryClient]
  );
}

export function useCreateOrder() {
  const queryClient = useQueryClient();
  const { storeService } = storeLayout.useRouteContext();
  const { addErrorAlert } = useContext(AlertContext);

  const { mutateAsync: createOrder } = useMutation({
    mutationFn: ({ storeId, order }: { storeId: string; order: CreateOrder }) => {
      return storeService.createOrder(storeId, order).catch((error: Error) => {
        addErrorAlert(error.message, { cause: error });
        throw error;
      });
    },
    onMutate: async ({ storeId }) => {
      await queryClient.cancelQueries({ queryKey: ['stores', storeId, 'orders'] });

      const previousOrders = queryClient.getQueryData<Order[]>(['stores', storeId, 'orders']);

      // We can't optimistically add the order since we don't have the ID yet
      // Just cancel queries to prevent race conditions
      return { previousOrders, storeId };
    },
    onSuccess: (newOrder: Order) => {
      // Add the new order with server-generated ID
      queryClient.setQueryData(
        ['stores', newOrder.store_id, 'orders'],
        (oldOrders: Order[] = []) => [...oldOrders, newOrder]
      );
    },
    onError: (err, _variables, context) => {
      addErrorAlert(`Error creating order: ${err.message}`, { cause: err });

      if (context?.previousOrders && context.storeId) {
        queryClient.setQueryData(['stores', context.storeId, 'orders'], context.previousOrders);
      }
    },
  });

  return (storeId: string, order: CreateOrder) => {
    return createOrder({ storeId, order });
  };
}

export function useMaybeCreateOrder() {
  const { draftOrdersByVendor } = useDraftOrders();
  const createOrder = useCreateOrder();
  const navigate = useNavigate();

  return async (store: Store, vendor: Vendor) => {
    const existingDraftOrders = draftOrdersByVendor(vendor.vendor_id);

    // If there are pending orders for this vendor we want the user to clean these up instead of creating another one
    if (existingDraftOrders.length > 0) {
      navigate({
        to: '/stores/$storeId/orders/cleanup',
        params: { storeId: store.id },
        search: { vendorId: vendor.vendor_id },
      });

      return;
    }

    const order = await createOrder(store.id, {
      vendor_id: vendor.vendor_id,
      items: [],
    });
    navigate({
      to: '/stores/$storeId/orders/$orderId/edit',
      params: { storeId: store.id, orderId: order.id },
    });
  };
}

export function useSaveOrderOptions() {
  const queryClient = useQueryClient();
  const { storeService } = storeLayout.useRouteContext();
  const { addErrorAlert } = useContext(AlertContext);

  const { mutateAsync: saveOrderOptions } = useMutation({
    mutationFn: ({
      storeId,
      orderId,
      options,
    }: {
      storeId: string;
      orderId: string;
      options: OrderOptions;
    }) => {
      return storeService.saveOrderOptions(storeId, orderId, options).catch((error: Error) => {
        addErrorAlert(error.message, { cause: error });

        throw error;
      });
    },
    onMutate: async ({ storeId, orderId, options }) => {
      await queryClient.cancelQueries({ queryKey: ['stores', storeId, 'orders'] });

      const previousOrders = queryClient.getQueryData<Order[]>(['stores', storeId, 'orders']);

      // Optimistically update
      queryClient.setQueryData(['stores', storeId, 'orders'], (oldOrders: Order[] = []) => {
        return oldOrders.map((order) => {
          if (order.id === orderId) {
            return { ...order, options };
          }

          return order;
        });
      });

      return { previousOrders, storeId };
    },
    onError: (err, _variables, context) => {
      addErrorAlert(`Error saving order options: ${err.message}`, { cause: err });

      if (context?.previousOrders) {
        queryClient.setQueryData(['stores', context.storeId, 'orders'], context.previousOrders);
      }
    },
  });

  // Keep this function since it has validation logic
  return (storeId: string, orderId: string, options: OrderOptions) => {
    return saveOrderOptions({ storeId, orderId, options });
  };
}

export function useSaveOrderItem() {
  const queryClient = useQueryClient();
  const { storeService } = storeLayout.useRouteContext();
  const { addErrorAlert } = useContext(AlertContext);

  const { mutateAsync: saveOrderItem } = useMutation({
    mutationFn: ({
      storeId,
      orderId,
      orderItem,
    }: {
      storeId: string;
      orderId: string;
      orderItem: OrderItem;
    }) => {
      return storeService.updateOrderItem(storeId, orderId, orderItem).catch((error: Error) => {
        addErrorAlert(error.message, { cause: error });

        throw error;
      });
    },
    onMutate: async ({ storeId, orderId, orderItem }) => {
      await queryClient.cancelQueries({ queryKey: ['stores', storeId, 'orders'] });

      const previousOrders = queryClient.getQueryData<Order[]>(['stores', storeId, 'orders']);

      // Optimistically update
      queryClient.setQueryData(['stores', storeId, 'orders'], (oldOrders: Order[] = []) => {
        return oldOrders.map((order) => {
          if (order.id === orderId) {
            return {
              ...order,
              items: order.items.map((i) =>
                i.product.id === orderItem.product.id ? orderItem : i
              ),
            };
          }

          return order;
        });
      });

      return { previousOrders, storeId };
    },
    onError: (err, _variables, context) => {
      addErrorAlert(`Error saving order item: ${err.message}`, { cause: err });

      if (context?.previousOrders) {
        queryClient.setQueryData(['stores', context.storeId, 'orders'], context.previousOrders);
      }
    },
  });

  return (storeId: string, order: Order, orderItem: OrderItem) => {
    return saveOrderItem({ storeId, orderId: order.id, orderItem });
  };
}

export function useSubmitOrder() {
  const queryClient = useQueryClient();
  const { storeService } = storeLayout.useRouteContext();
  const { addErrorAlert } = useContext(AlertContext);

  const { mutateAsync: submitOrder } = useMutation({
    mutationFn: ({ storeId, order, method }: { storeId: string; order: Order; method: string }) => {
      return storeService.submitOrder(storeId, order, method).catch((error: Error) => {
        addErrorAlert(error.message, { cause: error });

        throw error;
      });
    },
    onMutate: async ({ storeId, order }) => {
      await queryClient.cancelQueries({ queryKey: ['stores', storeId, 'orders'] });

      const previousOrders = queryClient.getQueryData<Order[]>(['stores', storeId, 'orders']);

      // Optimistically update order status (assuming it will be sent)
      queryClient.setQueryData(['stores', storeId, 'orders'], (oldOrders: Order[] = []) => {
        return oldOrders.map((o) =>
          o.id === order.id
            ? { ...order, status: OrderStatus.Sending } // Assuming sending status
            : o
        );
      });

      return { previousOrders, storeId };
    },
    onSuccess: (updatedOrder: Order, { storeId }) => {
      // Update with actual server response
      queryClient.setQueryData(['stores', storeId, 'orders'], (oldOrders: Order[] = []) => {
        return oldOrders.map((o) => (o.id === updatedOrder.id ? updatedOrder : o));
      });
    },
    onError: (err, _variables, context) => {
      addErrorAlert(`Error submitting order: ${err.message}`, { cause: err });

      if (context?.previousOrders) {
        queryClient.setQueryData(['stores', context.storeId, 'orders'], context.previousOrders);
      }
    },
  });

  return (storeId: string, order: Order, method: string) => {
    return submitOrder({ storeId, order, method });
  };
}

export function useDownloadOrder() {
  const { storeService } = storeLayout.useRouteContext();
  const { addErrorAlert } = useContext(AlertContext);

  return useCallback(
    (storeId: string, orderId: string, part?: number) => {
      storeService.downloadOrder(storeId, orderId, part).catch((error: Error) => {
        addErrorAlert(`Error downloading order: ${error.message}`, { cause: error });
      });
    },
    [storeService, addErrorAlert]
  );
}

export function useDeleteOrder() {
  const queryClient = useQueryClient();
  const { storeService } = storeLayout.useRouteContext();
  const { addErrorAlert } = useContext(AlertContext);

  const { mutateAsync: deleteOrder } = useMutation({
    mutationFn: (order: Order) => {
      return storeService.deleteOrder(order).catch((error: Error) => {
        addErrorAlert(error.message, { cause: error });

        throw error;
      });
    },
    onMutate: async (order: Order) => {
      await queryClient.cancelQueries({ queryKey: ['stores', order.store_id, 'orders'] });

      const previousOrders = queryClient.getQueryData<Order[]>([
        'stores',
        order.store_id,
        'orders',
      ]);

      // Optimistically remove the order
      queryClient.setQueryData(['stores', order.store_id, 'orders'], (oldOrders: Order[] = []) =>
        oldOrders.filter((o) => o.id !== order.id)
      );

      return { previousOrders, storeId: order.store_id };
    },
    onError: (err, _order, context) => {
      addErrorAlert(`Error deleting order: ${err.message}`, { cause: err });

      if (context?.previousOrders) {
        queryClient.setQueryData(['stores', context.storeId, 'orders'], context.previousOrders);
      }
    },
  });

  return deleteOrder;
}

export function useBulkDeleteOrders() {
  const queryClient = useQueryClient();
  const { addErrorAlert } = useContext(AlertContext);
  const { store, storeService } = storeLayout.useRouteContext();

  const { mutateAsync: bulkDeleteOrders } = useMutation({
    mutationFn: async (deleteOrders: Order[]) => {
      return Promise.all(
        deleteOrders.map((order) => {
          return storeService.deleteOrder(order).catch((error: Error) => {
            addErrorAlert(error.message, { cause: error });
            throw error;
          });
        })
      );
    },
    onMutate: async (deleteOrders: Order[]) => {
      await queryClient.cancelQueries({ queryKey: ['stores', store.id, 'orders'] });

      const previousOrders = queryClient.getQueryData<Order[]>(['stores', store.id, 'orders']);

      // Optimistically remove orders
      queryClient.setQueryData(['stores', store.id, 'orders'], (oldOrders: Order[] = []) => {
        return oldOrders.filter((o) => !deleteOrders.some((e) => e.id === o.id));
      });

      return { previousOrders, storeId: store.id };
    },
    onError: (err, _variables, context) => {
      addErrorAlert(`Error deleting orders: ${err.message}`, { cause: err });

      if (context?.previousOrders && context.storeId) {
        queryClient.setQueryData(['stores', context.storeId, 'orders'], context.previousOrders);
      }
    },
  });

  return bulkDeleteOrders;
}

// The factory the three v2 order mutations share (ORD-265). Deliberately does
// NOT repeat the shape of the six mutations above (cancel reads, snapshot,
// patch optimistically, roll back on error): on the v2 path the response IS
// the authoritative order, thanks to the server-owned version counter, so
// there is nothing to snapshot or roll back. What makes a typed edit visible
// before the response lands is the pending-edit overlay in
// use-order-items-v2, not an optimistic cache patch here.
function useOrderMutationV2<TVariables extends { storeId: string; orderId: string }>(
  mutationFn: (variables: TVariables) => Promise<Order>,
  errorLabel: string
) {
  const queryClient = useQueryClient();
  const { addErrorAlert } = useContext(AlertContext);

  return useMutation({
    mutationFn,
    onSuccess: (order, { storeId, orderId }) => {
      // The v2 response IS the authoritative order. Write it to both caches
      // rather than invalidating: we already hold newer data than a refetch
      // would produce, and an invalidate here would race the WS push (MR3).
      // writeOrderCache applies the newerOrder version gate — items and
      // options edits run through separate mutations with independent
      // FlushGuards, so an earlier-dispatched PATCH resolving last must not
      // clobber the other's already-cached, newer response.
      writeOrderCache(queryClient, storeId, orderId, order);
    },
    onError: (error: Error) => {
      // Both v2-order 409s are caller-owned recovery paths, not failures for
      // this generic toast to report: the overlay hook rebases onto the
      // version-conflict response, and navigates away with its own specific
      // alert on the not-a-draft one. Toasting either here would duplicate
      // that more specific handling. classifyOrderConflict is shared with
      // use-order-items-v2's handleFlushError so the two can't drift — and it
      // classifies on the body's `code`, so a 409 neither of them knows how to
      // recover from still lands here and gets reported.
      if (classifyOrderConflict(error) !== null) {
        return;
      }

      addErrorAlert(`${errorLabel}: ${error.message}`, { cause: error });
    },
  });
}

export function useBatchSetOrderItems() {
  const { storeService } = storeLayout.useRouteContext();

  return useOrderMutationV2(
    ({ storeId, orderId, updates }: { storeId: string; orderId: string; updates: ItemUpdate[] }) =>
      storeService.batchSetOrderItems(storeId, orderId, updates),
    'Error saving order items'
  );
}

export function useAllocateOrderDeal() {
  const { storeService } = storeLayout.useRouteContext();

  return useOrderMutationV2(
    ({
      storeId,
      orderId,
      familyId,
      cases,
    }: {
      storeId: string;
      orderId: string;
      familyId: string;
      cases: number;
    }) => storeService.allocateOrderDeal(storeId, orderId, familyId, cases),
    'Error allocating deal'
  );
}

export function useSetOrderOptionsV2() {
  const { storeService } = storeLayout.useRouteContext();

  return useOrderMutationV2(
    ({ storeId, orderId, options }: { storeId: string; orderId: string; options: OrderOptions }) =>
      storeService.setOrderOptions(storeId, orderId, options),
    'Error saving order settings'
  );
}
